/**
 * Formatting helpers that reproduce the .NET format strings the Razor pages used.
 *
 * Dates are rendered in UTC on purpose: the Razor portal called ToString() directly on
 * DateTime values read from Postgres (Kind = Utc) with no timezone conversion, so
 * formatting in the browser's local zone would silently shift every timestamp.
 */

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  // A payload without a zone suffix is a UTC instant from the API; make that explicit
  // so the browser does not read it as local time.
  const normalised = /Z|[+-]\d{2}:?\d{2}$/.test(value) ? value : `${value}Z`;
  const date = new Date(normalised);
  return Number.isNaN(date.getTime()) ? null : date;
}

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

/** .NET "MMM dd, yyyy" - e.g. "Aug 09, 2026". */
export function formatDate(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  return `${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCDate())}, ${d.getUTCFullYear()}`;
}

/** .NET "h:mm:ss tt" in UTC - e.g. "3:51:11 AM". */
export function formatClockTime(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  const h = d.getUTCHours();
  return `${h % 12 || 12}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} ${h < 12 ? 'AM' : 'PM'}`;
}

/** .NET "MMM dd, yyyy HH:mm" - e.g. "Aug 09, 2026 14:05". */
export function formatDateTime(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  return `${formatDate(value)} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** .NET "MMM dd, HH:mm" - e.g. "Aug 09, 14:05". */
export function formatShortDateTime(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  return `${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCDate())}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** .NET "MMM dd, HH:mm:ss" - the audit log needs second precision. */
export function formatTimestamp(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  return `${MONTHS[d.getUTCMonth()]} ${pad(d.getUTCDate())}, ${pad(d.getUTCHours())}:${pad(
    d.getUTCMinutes(),
  )}:${pad(d.getUTCSeconds())}`;
}

/** ISO calendar date - .NET "yyyy-MM-dd". Also the value format an <input type="date"> expects. */
export function formatIsoDate(value: string | null | undefined, fallback = ''): string {
  const d = parse(value);
  if (!d) return fallback;
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** .NET "N0" - thousands separators, no decimals. */
export function formatCount(value: number): string {
  return (value ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

/** .NET "F1" / "F0" - fixed decimal places, no thousands separator. */
export function formatFixed(value: number, decimals: number): string {
  return (value ?? 0).toFixed(decimals);
}

/** A 0-1 score rendered as a whole-number percentage, matching (x * 100).ToString("F0"). */
export function percent(value: number, decimals = 0): string {
  return ((value ?? 0) * 100).toFixed(decimals);
}

/**
 * Confidence bar colour thresholds, identical to the Razor markup and site.js:
 * >= 0.75 high, >= 0.5 medium, otherwise low.
 */
export function confidenceClass(value: number): string {
  if (value >= 0.75) return 'confidence-high';
  if (value >= 0.5) return 'confidence-medium';
  return 'confidence-low';
}

/**
 * A KYC session that scanned only the back (ExtractionMethod.BarcodeOnly): no front was OCR'd,
 * so there is no Average Confidence to show — N/A, not 0%.
 */
export function isBarcodeOnly(extractionMethod: string | null | undefined): boolean {
  return extractionMethod === 'BarcodeOnly';
}

/** Text colour for a confidence figure, matching the detail/review pages. */
export function confidenceTextClass(value: number): string {
  if (value >= 0.75) return 'text-success';
  if (value >= 0.5) return 'text-warning';
  return 'text-danger';
}

/**
 * Risk score runs the other way: high is bad. Four bands in the severity badge colours, cut at
 * the severity weights, so one finding shows in its own severity's colour (Critical 0.90,
 * High 0.60, Medium 0.30, Low 0.10 — FraudRecorder.Weight).
 */
export function riskTextClass(value: number): string {
  if (value >= 0.9) return 'risk-critical';
  if (value >= 0.6) return 'risk-high';
  if (value >= 0.3) return 'risk-medium';
  return 'risk-low';
}

/**
 * Hover text on an Applicant cell that holds a stand-in (reference, masked phone or short id)
 * rather than a name. Kept identical to `ApplicantLabel.PlaceholderHint` in Core, which also
 * decides the stand-in itself; the API sends it ready-made with `applicantIsName`.
 */
export const APPLICANT_PLACEHOLDER_HINT = "No name yet: the customer's ID has not been read.";

/** Joins the non-empty parts of a list with a separator, like string.Join over a Where filter. */
export function joinNonEmpty(parts: Array<string | null | undefined>, separator = ' '): string {
  return parts.filter((p): p is string => !!p && p.trim().length > 0).join(separator);
}

/** .NET "N2" - fixed decimals WITH thousands separators. Use for money. */
export function formatMoney(value: number, decimals = 2): string {
  return (value ?? 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

/** .NET "0.####" - up to four decimals, trailing zeros trimmed. Use for per-unit rates. */
export function formatRate(value: number): string {
  return (value ?? 0).toLocaleString('en-US', { maximumFractionDigits: 4 });
}
